import React from 'react';

import { Box, Typography, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ErrorOutline, Warning, Info, CheckCircle } from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  container: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: '60vh',
    padding: theme.spacing(4),
  },
  errorCard: {
    borderRadius: 8,
    maxWidth: 500,
    padding: theme.spacing(4),
    textAlign: 'center',
    width: '100%',
  },
  errorCardWarning: {
    backgroundColor: '#FFFBEB',
    border: '1px solid #FDE68A',
  },
  errorIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(2),
  },
  errorIconError: {
    color: '#E53E3E',
  },
  errorIconInfo: {
    color: '#3182CE',
  },
  errorIconSuccess: {
    color: '#38A169',
  },
  errorIconWarning: {
    color: '#D69E2E',
  },
  errorMessage: {
    color: '#4A5568',
    fontSize: 16,
    lineHeight: 1.5,
    marginBottom: theme.spacing(3),
  },
  errorTitle: {
    color: '#2D3748',
    fontSize: 24,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  helpText: {
    color: '#718096',
    fontSize: 14,
    lineHeight: 1.5,
    marginTop: theme.spacing(2),
  },
}));

const AccessDenied = ({ title = 'Access Denied', message = 'You do not have access to this quiz collection.', showHelpText = false, onGoBack, severity = 'error', reason }) => {
  const classes = useStyles();

  const getIcon = () => {
    if (reason === 'already_completed') {
      return <CheckCircle className={`${classes.errorIcon} ${classes.errorIconSuccess}`} />;
    }

    switch (severity) {
      case 'warning':
        return <Warning className={`${classes.errorIcon} ${classes.errorIconWarning}`} />;
      case 'info':
        return <Info className={`${classes.errorIcon} ${classes.errorIconInfo}`} />;
      default:
        return <ErrorOutline className={`${classes.errorIcon} ${classes.errorIconError}`} />;
    }
  };

  const getCardClass = () => {
    if (reason === 'already_completed') {
      return `${classes.errorCard} ${classes.errorCardSuccess}`;
    }

    switch (severity) {
      case 'warning':
        return `${classes.errorCard} ${classes.errorCardWarning}`;
      case 'info':
        return `${classes.errorCard} ${classes.errorCardInfo}`;
      default:
        return `${classes.errorCard}`;
    }
  };

  const getMessageForReason = () => {
    switch (reason) {
      case 'already_completed':
        return 'You have already completed this quiz. Thank you for your participation!';
      case 'invitation_expired':
        return 'Your invitation to this quiz has expired. Please contact your supervisor for a new invitation.';
      case 'no_invitation':
        return 'You do not have access to this quiz collection.';
      default:
        return message;
    }
  };

  const getTitleForReason = () => {
    switch (reason) {
      case 'already_completed':
        return 'Quiz Done';
      case 'invitation_expired':
        return 'Invitation Expired';
      default:
        return title;
    }
  };

  return (
    <Box className={classes.container}>
      <Box className={getCardClass()}>
        {getIcon()}

        <Typography className={classes.errorTitle}>{getTitleForReason()}</Typography>

        <Typography className={classes.errorMessage}>{getMessageForReason()}</Typography>

        {onGoBack && (
          <Button onClick={onGoBack} variant="contained" color="primary">
            GO BACK
          </Button>
        )}

        {showHelpText && reason !== 'already_completed' && (
          <Typography className={classes.helpText}>If you believe you should have access to this quiz, please contact your supervisor or administrator.</Typography>
        )}
      </Box>
    </Box>
  );
};

export default AccessDenied;
